import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, MapPin, Mail, Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/logo-main.png";

const Layout = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const serviceLinks = [
    { to: "/services/paint-protection-film", label: "Paint Protection Film" },
    { to: "/services/ceramic-coatings", label: "Ceramic Coating" },
    { to: "/services/window-tint", label: "Window Tint" },
    { to: "/services/vinyl-wraps", label: "Vinyl Wrap" },
    { to: "/services/windshield-protection", label: "Windshield Protection" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img src={logo} alt="Battle Born Protection Films" className="h-[60px] w-auto" width="129" height="60" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/") && location.pathname === "/" ? "text-accent" : "text-foreground"
                }`}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/services") ? "text-accent" : "text-foreground"
                }`}
              >
                Services
              </Link>
              <Link 
                to="/gallery" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/gallery") ? "text-accent" : "text-foreground"
                }`}
              >
                Gallery
              </Link>
              <Link 
                to="/resources" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/resources") ? "text-accent" : "text-foreground"
                }`}
              >
                Resources
              </Link>
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-colors hover:text-accent ${
                  isActive("/about") ? "text-accent" : "text-foreground"
                }`}
              >
                About
              </Link>
              <a
                href="tel:+17025599365"
                className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-accent transition-colors"
              >
                <Phone className="w-4 h-4 text-accent" />
                (702) 559-9365
              </a>
              <Button asChild variant="premium" size="sm">
                <Link to="/contact">Request Free Quote</Link>
              </Button>
            </nav>

            {/* Mobile: hamburger + CTA */}
            <div className="flex md:hidden items-center gap-3">
              <Button asChild variant="premium" size="sm">
                <Link to="/contact">Get Quote</Link>
              </Button>
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2 text-foreground hover:text-accent transition-colors"
                aria-label="Open navigation menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />

          {/* Slide-in panel */}
          <nav className="absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-card border-l border-border flex flex-col animate-slide-in-right overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <a
                href="tel:+17025599365"
                className="flex items-center gap-2 text-accent font-semibold text-sm"
              >
                <Phone className="w-4 h-4" />
                (702) 559-9365
              </a>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close navigation menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Links */}
            <div className="flex flex-col py-4 px-6 space-y-1 flex-1">
              <Link
                to="/"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                Home
              </Link>

              {/* Services with sub-links */}
              <div>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center justify-between w-full py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                {servicesOpen && (
                  <div className="pl-4 pb-2 space-y-1 animate-fade-in">
                    <Link
                      to="/services"
                      className="block py-2 text-sm text-muted-foreground hover:text-accent transition-colors"
                    >
                      All Services
                    </Link>
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block py-2 text-sm text-muted-foreground hover:text-accent transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/tesla-protection"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                Tesla Protection
              </Link>
              <Link
                to="/gallery"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                Gallery
              </Link>
              <Link
                to="/resources"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                Resources
              </Link>
              <Link
                to="/about"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="py-3 text-base font-medium text-foreground hover:text-accent transition-colors"
              >
                Contact
              </Link>
            </div>

            {/* CTA at bottom */}
            <div className="px-6 pb-8 pt-4 border-t border-border">
              <Button asChild variant="hero" size="lg" className="w-full">
                <Link to="/contact">GET QUOTE</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Contact Us</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                  <span>Battle Born Protection Films<br />6960 W Russell Rd B<br />Las Vegas, NV 89113</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-accent" />
                  <a href="tel:+17025599365" className="hover:text-accent transition-colors">
                    (702) 559-9365
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-accent" />
                  <a href="mailto:battlebornppf@gmail.com" className="hover:text-accent transition-colors">
                    battlebornppf@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Our Services</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <Link to="/services/paint-protection-film" className="hover:text-accent transition-colors">
                    Paint Protection Film
                  </Link>
                </li>
                <li>
                  <Link to="/services/ceramic-coatings" className="hover:text-accent transition-colors">
                    Ceramic Coatings
                  </Link>
                </li>
              <li>
                <Link to="/services/window-tint" className="hover:text-accent transition-colors">
                  Ceramic Window Tint
                </Link>
              </li>
              <li>
                <Link to="/services/vinyl-wraps" className="hover:text-accent transition-colors">
                  Vinyl Wraps & Graphics
                </Link>
              </li>
              <li>
                <Link to="/services/windshield-protection" className="hover:text-accent transition-colors">
                  Windshield Protection
                </Link>
              </li>
              </ul>
            </div>

            {/* Hours */}
            <div>
              <h3 className="text-lg font-heading font-semibold mb-4 text-accent">Hours of Operation</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Mon–Fri:</span>
                  <span>9:00 AM – 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sat:</span>
                  <span>10:00 AM – 3:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sun:</span>
                  <span>By Appointment Only</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Battle Born Protection Films. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Mobile floating call button */}
      <a
        href="tel:+17025599365"
        className="fixed bottom-6 right-6 z-50 md:hidden flex items-center justify-center w-14 h-14 rounded-full bg-accent shadow-lg shadow-accent/30 hover:brightness-110 transition-all"
        aria-label="Call (702) 559-9365"
      >
        <Phone className="w-6 h-6 text-white" />
      </a>
    </div>
  );
};

export default Layout;
